/**
 * Failure taxonomy and user-facing failure resolution for the capture pipeline.
 */
import {
  canonicalFailedAtFromRaw,
  canonicalLabelFromStageIndex,
  coerceFailedStageIndex,
  clampFailedPercentForStageIndex,
  CANONICAL_FAILED_AT,
  isCanonicalFailedAtLabel,
  failedStageIndexFromLabel,
  isVerboseFailureText,
  isSpecificUserFacingFailureDetail,
  isWatchdogGenericFailureDetail,
  resolveFailureStageFromProcessingContext,
} from '../../utils/failureMetadata';
import {
  failedStageIndexToUiSegment,
  SPARSE_SFM_FAILURE_CODE,
} from '../../constants/captureSchema';
import { SFM_PERCENT_END } from '../../utils/pipeline/pipelinePercentConstants';
import {
  coerceFrameInt,
  deriveFailedStageFromFrameCounts,
  deriveSfmSparseFailureMetadata,
  isSfmTooSparseFromFrameCounts,
  readCaptureFrameFields,
  MIN_REGISTERED_CAMERAS,
} from '../../utils/pipeline/pipelineFrameFields';
import {
  getFailedStageIndex,
  normalizeFailedStageIndexToUiSegment,
  normalizeStatusTitle,
  stageIndexToLabel,
  isActivePipelineRunStatus,
  isExhaustiveRetryInFlight,
} from './stageMapping.js';

/** Structured failure codes that indicate an internal/system fault (not capture quality). */
export const INTERNAL_FAILURE_CODES = new Set([
  'SFM_INTERNAL_ERROR',
  'WORKER_CRASH',
  'DEPENDENCY_MISSING',
  'OOM_OR_KILLED',
  'CUDA_OOM',
  'UNKNOWN',
]);

/** Infra SfM failures — never show sparse capture-quality copy at the SfM stage. */
export const SFM_INFRA_SPARSE_HINT_SUPPRESS_CODES = new Set([
  'SFM_INTERNAL_ERROR',
  'DEPENDENCY_MISSING',
  'OOM_OR_KILLED',
  'CUDA_OOM',
  'WORKER_CRASH',
]);

/** Transient infra failures — user should retry shortly, not recapture. */
export const TRANSIENT_FAILURE_CODES = new Set([
  'SUBPROCESS_STALL',
]);

/** Dedicated LoadingUI copy for unexpected internal pipeline errors. */
export const UNEXPECTED_INTERNAL_FAILURE_MESSAGE =
  'An unexpected error occurred unrelated to your video input. Please try again. If it keeps happening,';

export const SFM_INTERNAL_ERROR_MESSAGE =
  'We hit an internal error while building the 3D scene from your capture. '
  + 'This is not a problem with your video. Please try again. If it keeps happening,';

export const TRANSIENT_FAILURE_MESSAGE =
  'Processing stalled temporarily. Please try again in a few minutes.';
/** User-visible message when an in-tab upload cannot be resumed (reload / tab discard). */
export const UPLOAD_INTERRUPTED_MESSAGE = 'Upload interrupted. Please try again.';

/** Grey hint copy while client poll is uncertain but backend has not declared failure. */
export const STALE_POLL_CHECKING_MESSAGE = 'Still checking processing status. This can take a moment.';

/** Layman failure copy for LoadingUI grey hint callouts (not the black pill). */
export const FAILURE_CALLOUT_MESSAGES = {
  DISPATCH: "We uploaded your video, but couldn't start processing it. Please try again.",
  GPU_UNAVAILABLE: 'No GPU became available in time. Your capture is safe — try again.',
  WORKER_STOPPED:
    'Processing has unexpectedly stopped before it could finish. We are sorry and know it is frustrating. '
    + 'If this keeps occurring,',
  UPLOAD_NETWORK:
    'Your video upload lost connection before it finished. Check your internet connection and try again.',
  STORAGE_CAP:
    'Your capture storage is full. Delete old captures or upgrade storage, then try again.',
  INSV_INGEST_DISABLED:
    'Raw INSV processing is not enabled on our servers yet. Export a stitched 2:1 equirect MP4 from Insta360 Studio or Antigravity and upload that instead.',
  INSV_STITCH_FAILED:
    'We could not stitch your INSV files into a 360 video. Re-export from Insta360 Studio as a stitched 2:1 equirect MP4, or upload both _00 and _10 lens files from the same recording.',
  INSV_RAW_MISSING:
    'We could not find your uploaded INSV files in storage. Re-upload the .insv file(s) and try again.',
  INSV_METADATA_MISSING:
    'This capture is missing INSV file metadata. Re-upload the .insv file(s) from your 360 camera.',
  SOG_VIEWER_FAILED:
    'Your 3D model was reconstructed successfully, but we could not prepare the browser viewer. '
    + 'Download the PLY file from Export, or contact support.',
};

export function isPrecisionRetryEligible() {
  return false;
}

const STAGE_INDEX_SHORT = {
  0: 'Upload failed',
  1: 'Extraction failed',
  2: 'Analysis failed',
  3: 'Training failed',
};

function uiFailedStageSegment(failedStageIndex = -1) {
  const n = Number(failedStageIndex);
  if (!Number.isFinite(n) || n < 0) return -1;
  if (n >= 0 && n <= 3) return n;
  if (n >= 1 && n <= 5) return failedStageIndexToUiSegment(n);
  return -1;
}

function reasonFromStageIndex(idx) {
  const ui = uiFailedStageSegment(idx);
  if (ui >= 0 && STAGE_INDEX_SHORT[ui]) {
    return STAGE_INDEX_SHORT[ui];
  }
  return null;
}

/** Firestore processing.failedAt when RunPod redispatch cap is reached (matches Cloud Functions). */
export const GPU_WORKER_UNAVAILABLE_FAILED_AT = 'No healthy GPU worker available';

/** LoadingUI arc title for dispatch / queue / worker-timeout failures (not stage-index SfM failures). */
export const GPU_QUEUE_TIMEOUT_FAILURE_TITLE = 'NO GPU AVAILABLE';

const GPU_QUEUE_TIMEOUT_DETAIL_PATTERNS = [
  'timed out',
  'execution time limit',
  'no workers available',
  'worker stopped unexpectedly',
];

function reasonFromStageToken(raw) {
  const n = (raw || '').toLowerCase().replace(/\s/g, '');
  if (!n) return null;
  if (n === 'sfm') return 'Analysis failed';
  if (n.includes('gaussiantraining')) return 'Training failed';
  if (n.includes('gaussianexport') || n === 'export') return 'Export failed';
  if (n.includes('frameextraction')) return 'Extraction failed';
  if (n === 'upload' || n === 'uploadpending') return 'Upload failed';
  if (n === 'cancelled' || n === 'dispatch') return null;
  return null;
}

/** LoadingUI failure pill labels are always uppercase. */
function formatFailurePillLabel(label) {
  if (label == null || typeof label !== 'string' || !label.trim()) return label;
  return label.trim().toUpperCase();
}

/**
 * Default pill label when no phrase matches failureDetail — always stage-based, never "unknown".
 */
export function getFailureStageFallbackLabel(failedStageIndex = -1, failedAt = null) {
  const fromIdx = reasonFromStageIndex(failedStageIndex);
  if (fromIdx) return formatFailurePillLabel(fromIdx);

  if (failedAt != null && typeof failedAt === 'string' && failedAt.trim()) {
    const n = failedAt.trim().toLowerCase();
    if (n === 'dispatch') return formatFailurePillLabel('Dispatch failed');
    if (n === 'cancelled') return formatFailurePillLabel('Cancelled');
    const idxFromAt = getFailedStageIndex(failedAt);
    if (idxFromAt >= 0) {
      const fromAtIdx = reasonFromStageIndex(idxFromAt);
      if (fromAtIdx) return formatFailurePillLabel(fromAtIdx);
    }
  }

  return formatFailurePillLabel('Pipeline failed');
}

/** True when text is a canonical stage label (processing.failedAt), not an ops failure phrase. */
export function isStageOnlyFailureText(text) {
  if (text == null || typeof text !== 'string') return false;
  const t = text.trim();
  if (!t) return false;
  const n = t.toLowerCase();
  const stageLabels = new Set([
    CANONICAL_FAILED_AT.UPLOAD.toLowerCase(),
    CANONICAL_FAILED_AT.FRAME_EXTRACTION.toLowerCase(),
    CANONICAL_FAILED_AT.SFM.toLowerCase(),
    CANONICAL_FAILED_AT.GAUSSIAN_TRAINING.toLowerCase(),
    CANONICAL_FAILED_AT.DISPATCH.toLowerCase(),
    CANONICAL_FAILED_AT.CANCELLED.toLowerCase(),
    CANONICAL_FAILED_AT.UNKNOWN.toLowerCase(),
    'structure from motion',
    'frame extraction',
    'gaussian training',
  ]);
  if (stageLabels.has(n)) return true;
  if (Object.values(CANONICAL_FAILED_AT).includes(t)) return true;
  return false;
}

/**
 * Map ops `processing.failureDetail` (or client error text) to a short user-safe pill label.
 * Never returns raw detail text — only mapped categories or stage-index fallback.
 */
function resolveFailureReasonLabel(failureDetail, failedStageIndex = -1) {
  if (failureDetail == null || typeof failureDetail !== 'string' || !failureDetail.trim()) {
    return reasonFromStageIndex(failedStageIndex);
  }

  let s = failureDetail.trim();
  let n = s.toLowerCase();
  if (n.startsWith('error:')) {
    s = s.slice(6).trim();
    n = s.toLowerCase();
  }

  if (isStageOnlyFailureText(s)) {
    return reasonFromStageIndex(failedStageIndex);
  }

  if (n === 'internal' || n === 'functions/internal') {
    return reasonFromStageIndex(failedStageIndex);
  }

  if (n.includes('cancelled by runpod') || n === 'cancelled') return null;

  // Upload / client
  if (n.includes('upload interrupted')) return 'Upload interrupted';
  if (
    n.includes('storage check failed')
    || n.includes('storage headroom')
    || n.includes('storage quota')
    || n.includes('storage cap')
    || n.includes('captures storage')
  ) {
    return 'Storage limit';
  }
  if (n.includes('upload failed') || n.includes('video upload') || n.includes('download failed')) {
    return 'Upload failed';
  }

  // INSV / 360 stitch
  if (n.includes('insv ingest is not enabled')) return 'Stitch unavailable';
  if (n.includes('mediasdk binary not found') || n.includes('ffmpeg v360 filter not available')) {
    return 'Stitch unavailable';
  }
  if (n.includes('insv stitch failed') || n.includes('ffmpeg insv stitch failed')) return 'Stitch failed';
  if (n.includes('stitched output failed equirect validation') || n.includes('concatenated stitched output failed equirect validation')) {
    return 'Invalid stitch output';
  }
  if (n.includes('raw insv not found in storage')) return 'INSV files missing';
  if (n.includes('insvrawpaths') || n.includes('missing raw file metadata')) return 'INSV metadata missing';
  if (n.includes('insv segments do not align')) return 'INSV files mismatch';
  if (n.includes('dual-track clip detected')) return 'INSV format issue';

  // Network / backend
  if (
    n.includes('backend unreachable')
    || n.includes('gpu backend is unreachable')
    || n.includes('pod unreachable')
  ) {
    return 'Servers offline';
  }
  if (
    n.includes('offline')
    || n.includes('servers')
    || n.includes('network')
    || n.includes('fetch failed')
    || n.includes('backend server')
    || n.includes('connection refused')
    || n.includes('econnrefused')
  ) {
    return 'Servers offline';
  }
  if (n.includes('request timed out')) return 'Request timed out';

  // GPU / RunPod
  if (
    n.includes('runpod status: timed_out')
    || n.includes('execution time limit')
    || n.includes('time limit reached')
    || (n.includes('timed out') && !n.includes('queue'))
  ) {
    return 'GPU timed out';
  }
  if (
    n.includes('no healthy gpu worker')
    || n.includes('gpu worker unavailable')
    || n.includes('no active worker')
    || n.includes('no workers available')
    || n.includes('workers are busy')
  ) {
    return 'No GPU available';
  }
  if (n.includes('worker crashed')) return 'Worker crashed';
  if (
    n.includes('worker stopped unexpectedly')
    || n.includes('runpod status: failed')
    || n.includes('runpod status: error')
  ) {
    return 'Worker stopped';
  }
  if (n.includes('dispatch failed') || n.includes('job submission failed')) {
    return 'Dispatch failed';
  }
  if (n.includes('queue timeout') || n.includes('still waiting for a free gpu')) {
    return 'GPU queue timeout';
  }

  // Memory
  if (n.includes('cuda out of memory')) return 'GPU memory full';
  if (
    n.includes('out of memory')
    || n.includes('oom')
    || n.includes('signal 9')
    || (n.includes('killed') && n.includes('signal'))
  ) {
    return 'Out of memory';
  }
  if (n.includes('process killed') || n.includes('gpu ran out of memory')) return 'Out of memory';

  // SfM / input quality
  if (
    n.includes('too sparse')
    || n.includes('not enough registered')
    || n.includes('images registered')
    || n.includes('images matched')
    || n.includes('parallax/overlap')
    || (n.includes('parallax') && n.includes('overlap'))
  ) {
    return 'Not enough angles';
  }
  if (n.includes('too few') && n.includes('3d point')) return 'Too few points';
  if (
    (n.includes('points3d') || n.includes('points3'))
    && (n.includes('no readable') || n.includes('missing or invalid'))
  ) {
    return 'No 3D points';
  }
  if (
    n.includes('transforms.json')
    || n.includes('no usable camera')
    || n.includes('no camera data')
  ) {
    return 'No camera data';
  }
  if (n.includes('too few') && n.includes('match')) return 'Too few matches';
  if (n.includes('sparse') && (n.includes('colmap') || n.includes('reconstruction'))) {
    return 'Sparse reconstruction';
  }

  // Gaussian / export / viewer prep
  if (
    n.includes('gaussian export failed')
    || n.includes('no splat')
    || n.includes('no training config')
    || (n.includes('export') && n.includes('failed'))
    || n.includes('viewer prep')
    || n.includes('preparing viewer')
  ) {
    return 'Export failed';
  }
  if (n.includes('training failed') || (n.includes('gaussian') && n.includes('fail'))) {
    return 'Training failed';
  }

  if (n.startsWith('unknown:')) {
    if (n.includes('sfm')) return 'Analysis failed';
    if (n.includes('gaussiantraining')) return 'Training failed';
    if (n.includes('gaussianexport') || n.includes('export failed')) return 'Export failed';
    if (n.includes('frame')) return 'Extraction failed';
    return reasonFromStageIndex(failedStageIndex);
  }

  if (n === 'pipeline failed') return reasonFromStageIndex(failedStageIndex) || 'Pipeline failed';

  const fromToken = reasonFromStageToken(s);
  if (fromToken) return fromToken;

  if (n.includes('timed out')) return 'Timed out';
  if (n.includes('sfm') || n.includes('structure from motion') || n.includes('colmap')) return 'Analysis failed';
  if (n.includes('gaussian') || n.includes('generating')) return 'Training failed';
  if (
    n.includes('gaussian export failed')
    || n.includes('export failed')
    || (n.includes('[export]') && n.includes('fail'))
  ) {
    return 'Export failed';
  }
  if (n.includes('frame') && n.includes('extract')) return 'Extraction failed';
  if (n.includes('semantic') || n.includes('indexing')) return 'Indexing failed';

  return reasonFromStageIndex(failedStageIndex);
}

export function getFailureReasonFromDetail(failureDetail, failedStageIndex = -1) {
  const label = resolveFailureReasonLabel(failureDetail, failedStageIndex);
  return label ? formatFailurePillLabel(label) : null;
}

/** @deprecated Prefer getFailureReasonFromDetail — kept for call-site compatibility. */
export function getFailureShortReason(raw, failedStageIndex = -1) {
  if (raw == null || typeof raw !== 'string' || !raw.trim()) {
    return getFailureReasonFromDetail(null, failedStageIndex);
  }
  if (isStageOnlyFailureText(raw)) {
    return getFailureReasonFromDetail(null, failedStageIndex);
  }
  return getFailureReasonFromDetail(raw, failedStageIndex);
}

/**
 * Resolve the user-visible failure pill label for LoadingUI (2–3 words).
 * Primary source: `processing.failureDetail` phrases. Stage-only `failedAt` never becomes pill copy.
 *
 * @param {string|{ failureDetail?: string|null, failedAt?: string|null, failedStageIndex?: number, isFailed?: boolean }} input
 * @param {{ failedStageIndex?: number, isFailed?: boolean }} [legacyOptions]
 */
export function resolveFailureDisplayMessage(input, legacyOptions = {}) {
  let failureDetail = null;
  let failedAt = null;
  let failedStageIndex = -1;
  let isFailed = true;

  if (input != null && typeof input === 'object' && !Array.isArray(input)) {
    failureDetail = input.failureDetail ?? null;
    failedAt = input.failedAt ?? null;
    failedStageIndex = input.failedStageIndex ?? -1;
    isFailed = input.isFailed !== false;
    if (!failureDetail && failedAt && !isStageOnlyFailureText(failedAt)) {
      failureDetail = failedAt;
    }
  } else {
    const raw = input;
    failedStageIndex = legacyOptions.failedStageIndex ?? -1;
    isFailed = legacyOptions.isFailed !== false;
    if (raw != null && typeof raw === 'string' && raw.trim()) {
      if (isStageOnlyFailureText(raw)) {
        failedAt = raw.trim();
      } else {
        failureDetail = raw.trim();
      }
    }
  }

  if (!isFailed) return null;
  const reason = getFailureReasonFromDetail(failureDetail, failedStageIndex);
  if (reason) return reason;
  return getFailureStageFallbackLabel(failedStageIndex, failedAt);
}

function isStageFallbackPillLabel(label, failedStageIndex = -1, failedAt = null) {
  if (!label || typeof label !== 'string') return true;
  const fallback = getFailureStageFallbackLabel(failedStageIndex, failedAt);
  return label.trim().toUpperCase() === String(fallback || '').trim().toUpperCase();
}

/**
 * Short ALL-CAPS failure label for LoadingUI black pill and capture-card overlay.
 * Mirrors LoadingUI `displayPillMessage` while checking progress/poll text when
 * `processing.failureDetail` is missing or only yields a generic stage fallback.
 */
function isProgressFailureLabelCompatibleWithStage(label, failedStageIndex = -1) {
  const ui = uiFailedStageSegment(failedStageIndex);
  if (ui < 0 || !label) return true;
  const n = String(label).trim().toUpperCase();
  if (ui <= 2 && (n.includes('EXPORT') || n.includes('TRAINING FAILED'))) return false;
  if (ui === 0 && (n.includes('EXPORT') || n.includes('TRAINING') || n.includes('ANALYSIS'))) {
    return false;
  }
  return true;
}

export function resolveFailurePillLabel({
  failureDetail = null,
  progressMessage = null,
  polledFailedAt = null,
  uploadError = null,
  failedAt = null,
  failedStageIndex = -1,
  stageTitle = null,
} = {}) {
  const uiFailedStageIndex = normalizeFailedStageIndexToUiSegment(failedStageIndex, failedAt);
  const primary = resolveFailureDisplayMessage({
    failureDetail: failureDetail ?? uploadError,
    failedAt,
    failedStageIndex: uiFailedStageIndex,
  });
  if (primary && !isStageFallbackPillLabel(primary, uiFailedStageIndex, failedAt)) {
    return primary;
  }

  for (const extra of [progressMessage, polledFailedAt]) {
    if (extra == null || typeof extra !== 'string' || !extra.trim()) continue;
    const fromExtra = resolveFailureDisplayMessage({
      failureDetail: extra,
      failedAt,
      failedStageIndex: uiFailedStageIndex,
    });
    if (fromExtra && !isStageFallbackPillLabel(fromExtra, uiFailedStageIndex, failedAt)) {
      if (!isProgressFailureLabelCompatibleWithStage(fromExtra, uiFailedStageIndex)) continue;
      return fromExtra;
    }
  }

  return primary
    || getFailureStageFallbackLabel(uiFailedStageIndex, failedAt)
    || normalizeStatusTitle(stageTitle)
    || 'HALTED';
}

function stripErrorPrefix(text) {
  let s = String(text || '').trim();
  if (s.toLowerCase().startsWith('error:')) {
    s = s.slice(6).trim();
  }
  return s;
}

/** Infer capture profile for failure copy when Firestore field is missing (legacy / retrain). */
export function resolveCaptureProfileForFailureHint({
  captureProfile = null,
  sfmBackend = null,
} = {}) {
  const raw = String(captureProfile || '').trim().toLowerCase();
  if (raw === 'orbital') return raw;
  if (raw === 'equirect_360' || raw === 'equirectangular' || raw === 'equirect') {
    return 'equirect_360';
  }
  return 'orbital';
}

/** Mirror backend `recapture_hint_for_profile` when verbose ops copy never reached Firestore. */
export function sfmRecaptureHintForProfile(captureProfile = null, opts = {}) {
  const profile = resolveCaptureProfileForFailureHint({
    captureProfile,
    sfmBackend: opts.sfmBackend,
  });
  if (profile === 'equirect_360') {
    return (
      '360 equirectangular processing failed. Re-upload a stitched 2:1 equirect MP4 exported from '
      + 'Insta360 or Antigravity Studio, move slowly with steady overlap, and avoid motion blur.'
    );
  }
  return (
    'Site or aerial SfM failed. Fly steady overlapping passes at consistent altitude, '
    + 'keep 60-90° overlap between flight lines and orbit passes, trim takeoff and landing '
    + 'wobble from the clip, and avoid motion blur. Re-upload a flat MP4 from your drone.'
  );
}

/** True when a terminal failure is the low-registration "SfM too sparse" capture-quality case. */
export function isSfmTooSparseFailure({
  failureDetail = null,
  failureHoverExplanation = null,
  failedAt = null,
  progressMessage = null,
  polledFailedAt = null,
} = {}) {
  const candidates = [failureDetail, failureHoverExplanation, failedAt, progressMessage, polledFailedAt];
  for (const raw of candidates) {
    if (raw == null || typeof raw !== 'string') continue;
    const n = raw.toLowerCase();
    if (n.includes('too sparse')) return true;
    if (n.includes('parallax/overlap')) return true;
    if (n.includes('coverage gate failed')) return true;
    if (n.includes('registration ratio') && n.includes('below minimum')) return true;
    // Progress heartbeats can mention match counts mid-SfM; only treat as failure with failure language.
    const hasMatchCounts = (
      (n.includes('images registered') || n.includes('images matched'))
      && (n.includes('sfm') || n.includes('/'))
    );
    if (!hasMatchCounts) continue;
    const looksFailed = (
      n.includes('fail')
      || n.includes('unable')
      || n.includes('too few')
      || n.includes('unsat')
      || n.includes('sparse')
    );
    if (looksFailed) return true;
  }
  return false;
}

/**
 * Sparse registration counts that reflect a finished SfM attempt — not mid-sequential drift.
 * Backend only writes framesRegistered after SfM completes (success or sparse failure), so sparse
 * counts during processing are terminal. Ignore stale counts while re-queued at 0%.
 */
export function isTerminalSparseSfmFrameEvidence({
  processingStatus = null,
  failedAt = null,
  failureDetail = null,
  failureCode = null,
  progressMessage = null,
  sfmMatchingMethod = null,
  framesExtracted = null,
  framesRegistered = null,
  proc = {},
} = {}) {
  if (isExhaustiveRetryInFlight({ processing: { ...proc, status: processingStatus ?? proc.status } })) {
    return false;
  }
  const extracted = coerceFrameInt(
    framesExtracted ?? readCaptureFrameFields(proc).framesExtracted,
  );
  const registered = coerceFrameInt(
    framesRegistered ?? readCaptureFrameFields(proc).framesRegistered,
  );
  const code = String(failureCode || proc.failureCode || '').trim().toUpperCase();
  const detail = failureDetail ?? proc.failureDetail ?? null;
  const hasSparseCounts = extracted != null && extracted > 0 && registered != null;
  const sparseByCounts = isSfmTooSparseFromFrameCounts(extracted, registered);
  const sparseByCode = code === SPARSE_SFM_FAILURE_CODE && hasSparseCounts;
  const sparseByText = isSfmTooSparseFailure({
    failureDetail: detail,
    failedAt: failedAt ?? proc.failedAt ?? null,
    progressMessage,
  });
  if (!sparseByCounts && !sparseByCode && !(sparseByText && hasSparseCounts)) return false;

  const status = (processingStatus || proc.status || '').toLowerCase();
  if (status === 'failed' || status === 'cancelled') return true;

  // Stale sparse counts can linger on a re-queued run before the worker clears them.
  if (status === 'queued') return false;

  if (code === SPARSE_SFM_FAILURE_CODE) return true;

  const at = failedAt ?? proc.failedAt ?? null;
  if (sparseByText) return true;
  if (at && String(at).trim()) return true;

  // Bare sparse counts during processing are terminal after SfM finishes (backend writes
  // framesRegistered only then). Ignore registered===0 with no failure markers — that can
  // flash at the IMAGE EXTRACTION → ANALYZING IMAGES handoff before SfM starts matching.
  if (status === 'processing') {
    return registered > 0;
  }

  return false;
}

/** True when capture metadata shows a terminal failure even if status has not caught up. */
export function hasCaptureTerminalFailureEvidence({
  processingStatus = null,
  failedAt = null,
  failureDetail = null,
  progressMessage = null,
  progress = null,
  proc = {},
} = {}) {
  if (isExhaustiveRetryInFlight({ processing: { ...proc, status: processingStatus ?? proc.status } })) {
    return false;
  }
  if (shouldSuppressStaleInfraFailureUi({
    processing: { ...proc, status: processingStatus ?? proc.status, failedAt, failureDetail },
    progress: progress ?? {},
  })) {
    return false;
  }
  const status = (processingStatus || '').toLowerCase();
  if (status === 'failed' || status === 'cancelled') return true;
  if (INTERNAL_FAILURE_CODES.has(String(proc.failureCode || '').trim().toUpperCase())) return true;
  if (isSfmTooSparseFailure({ failureDetail, failedAt, progressMessage })) return true;
  if (isActivePipelineRunStatus(status)) {
    if (isSpecificUserFacingFailureDetail(failureDetail, failedAt)) return true;
    return isTerminalSparseSfmFrameEvidence({
      processingStatus: status,
      failedAt,
      failureDetail,
      failureCode: proc.failureCode,
      progressMessage,
      proc,
    });
  }
  if (failedAt && !isActivePipelineRunStatus(status)) return true;
  const { framesExtracted, framesRegistered } = readCaptureFrameFields(proc);
  if (deriveSfmSparseFailureMetadata({
    framesExtracted,
    framesRegistered,
    failureCode: proc.failureCode,
    failureDetail,
  })) return true;
  if (isSpecificUserFacingFailureDetail(failureDetail, failedAt)) return true;
  return false;
}

/** True for transient infra faults (retry shortly, not recapture). */
export function isTransientFailure({ failureCode = null, failureDetail = null } = {}) {
  const code = typeof failureCode === 'string' ? failureCode.trim().toUpperCase() : '';
  if (code && TRANSIENT_FAILURE_CODES.has(code)) return true;
  const detail = typeof failureDetail === 'string' ? failureDetail.toLowerCase() : '';
  return detail.includes('stalled with no output');
}

/**
 * Strip legacy inline support emails and detect when LoadingUI should show Contact us.
 * @returns {{ body: string, showContactAction: boolean }}
 */
export function normalizeFailureCalloutForDisplay(message) {
  const raw = String(message || '').trim();
  if (!raw) return { body: '', showContactAction: false };

  let body = raw
    .replace(/\s*—?\s*if it keeps happening,?\s*contact us at:?\s*support@volugraph\.com\.?\s*$/i, ' If it keeps happening,')
    .replace(/\s*if this keeps occurring,?\s*contact us at:?\s*support@volugraph\.com\.?\s*$/i, ' If this keeps occurring,')
    .replace(/\s*contact us at:?\s*support@volugraph\.com\.?\s*$/i, '')
    .trim();

  const showContactAction = /if this keeps occurring,?\s*$/i.test(body)
    || /if it keeps happening,?\s*$/i.test(body)
    || /support@volugraph\.com/i.test(raw);

  if (showContactAction) {
    if (/if this keeps occurring/i.test(body) && !/,?\s*$/i.test(body)) {
      body = body.replace(/\s*if this keeps occurring\.?\s*$/i, ' If this keeps occurring,');
    }
    if (/if it keeps happening/i.test(body) && !/,?\s*$/i.test(body)) {
      body = body.replace(/\s*if it keeps happening\.?\s*$/i, ' If it keeps happening,');
    }
  }

  return { body, showContactAction };
}

/** User-facing copy for internal/system failures. */
export function resolveUnexpectedInternalFailureMessage({
  failureCode = null,
  failureDetail = null,
} = {}) {
  const code = typeof failureCode === 'string' ? failureCode.trim().toUpperCase() : '';
  const detail = typeof failureDetail === 'string' ? failureDetail.trim() : '';
  if (code === 'SFM_INTERNAL_ERROR') {
    return SFM_INTERNAL_ERROR_MESSAGE;
  }
  if (code === 'CUDA_OOM') {
    return 'GPU ran out of memory during processing. Try a shorter clip or simpler scene.';
  }
  if (code === 'OOM_OR_KILLED') {
    return 'Processing ran out of memory or was stopped by the system. Try a shorter clip or try again later.';
  }
  if (code === 'DEPENDENCY_MISSING') {
    return FAILURE_CALLOUT_MESSAGES.WORKER_STOPPED;
  }
  if (detail.includes('not a problem with your video')) {
    return SFM_INTERNAL_ERROR_MESSAGE;
  }
  return UNEXPECTED_INTERNAL_FAILURE_MESSAGE;
}

/** True for internal/system faults that should freeze at the actual stage (not upload failure). */
export function isUnexpectedInternalFailure({
  failureCode = null,
  failureDetail = null,
  failedStageIndex = -1,
} = {}) {
  if (isTransientFailure({ failureCode, failureDetail })) return false;
  const code = typeof failureCode === 'string' ? failureCode.trim().toUpperCase() : '';
  if (code && INTERNAL_FAILURE_CODES.has(code)) {
    return failedStageIndex !== 0;
  }
  const detail = typeof failureDetail === 'string' ? failureDetail.toLowerCase() : '';
  if (detail === 'internal' || detail === 'functions/internal') {
    return failedStageIndex !== 0;
  }
  if (
    detail.includes('internal error')
    || detail.includes('einsum(')
    || detail.includes('worker crashed')
    || detail.includes('not a problem with your video')
  ) {
    return failedStageIndex !== 0;
  }
  return false;
}

/**
 * Canonical failure resolver — single source of truth for failed capture stage/percent/hints.
 * Evidence priority: frame counts > verbose failure text > failedAt label > explicit index > percent.
 */
export function resolveCanonicalFailure({
  processingStatus = null,
  failedAt = null,
  failureDetail = null,
  failureCode = null,
  sfmMatchingMethod = null,
  failedStageIndex: explicitStageIndex = null,
  failedPercent: explicitFailedPercent = null,
  progressMessage = null,
  progressPercent = null,
  polledFailedAt = null,
  polledFailedStageIndex = null,
  polledFailedPercent = null,
  framesRegistered = null,
  framesExtracted = null,
  pipelineStage = null,
  uploadError = null,
  proc = {},
} = {}) {
  const registered = coerceFrameInt(framesRegistered);
  const extracted = coerceFrameInt(framesExtracted);
  const status = (processingStatus || proc.status || '').toLowerCase();
  if (isExhaustiveRetryInFlight({
    processing: {
      ...proc,
      status,
      sfmMatchingMethod: sfmMatchingMethod ?? proc.sfmMatchingMethod ?? proc.sfm_matching_method,
      framesExtracted: extracted,
      framesRegistered: registered,
    },
  })) {
    return {
      isFailed: false,
      hasFailureEvidence: false,
      failedStageIndex: -1,
      failedStageLabel: '',
      failedAt: null,
      failedPercent: 0,
      isSfmTooSparse: false,
      sparseCounts: null,
      failureDetailText: null,
      showSfmFailureHint: false,
    };
  }
  const frameEvidence = deriveSfmSparseFailureMetadata({
    framesExtracted: extracted,
    framesRegistered: registered,
    failureCode,
    failureDetail,
  }) || deriveFailedStageFromFrameCounts(extracted, registered);
  const isTerminalStatus = status === 'failed' || status === 'cancelled';
  const hasUploadErr = typeof uploadError === 'string' && uploadError.trim().length > 0;
  const terminalSparseFrames = frameEvidence && isTerminalSparseSfmFrameEvidence({
    processingStatus: status,
    failedAt,
    failureDetail,
    failureCode,
    progressMessage,
    sfmMatchingMethod,
    framesExtracted: extracted,
    framesRegistered: registered,
    proc,
  });

  if (frameEvidence && (isTerminalStatus || !isActivePipelineRunStatus(status) || terminalSparseFrames)) {
    return {
      isFailed: true,
      hasFailureEvidence: true,
      failedStageIndex: failedStageIndexToUiSegment(frameEvidence.failedStageIndex),
      failedStageLabel: stageIndexToLabel(failedStageIndexToUiSegment(frameEvidence.failedStageIndex)),
      failedAt: frameEvidence.failedAt,
      failedPercent: frameEvidence.failedPercent,
      isSfmTooSparse: true,
      sparseCounts: { matched: registered, total: extracted },
      failureDetailText: frameEvidence.failureDetail,
      showSfmFailureHint: true,
    };
  }

  const sparseFromText = isSfmTooSparseFailure({
    failureDetail,
    failedAt,
    progressMessage,
    polledFailedAt,
  });

  let failedAtRaw = failedAt ?? polledFailedAt ?? null;
  let stageIndex = explicitStageIndex ?? polledFailedStageIndex ?? null;
  let pct = explicitFailedPercent ?? polledFailedPercent ?? progressPercent ?? 0;
  if (!Number.isFinite(pct)) pct = 0;

  const sogFailedLabel = String(failedAtRaw || failedAt || proc.failedAt || '').toLowerCase();
  const sogStageIndex = stageIndex ?? proc.failedStageIndex;
  if (
    isTerminalStatus
    && (
      sogFailedLabel.includes('sog')
      || Number(sogStageIndex) === 5
      || String(proc.failureDetail || failureDetail || '').toLowerCase().includes('browser viewer')
    )
  ) {
    return {
      isFailed: true,
      hasFailureEvidence: true,
      failedStageIndex: 3,
      failedStageLabel: 'Creating 3D Model',
      failedAt: 'SOG conversion',
      failedPercent: pct,
      isSfmTooSparse: false,
      sparseCounts: null,
      failureDetailText: FAILURE_CALLOUT_MESSAGES.SOG_VIEWER_FAILED,
      showSfmFailureHint: false,
    };
  }

  const canonicalStageOpts = {
    progressMessage,
    percent: pct,
    framesExtracted: extracted,
    framesRegistered: registered,
    processingStage: pipelineStage,
  };

  // The backend attributes a failure to a concrete pipeline stage (a numeric 0–3 index and/or a
  // canonical `failedAt` label). Trust that over substring-scanning the verbose failure detail:
  // an SfM recapture hint ("…Re-upload a standard flat video.") must never be reclassified as an
  // Upload-stage failure just because it contains the word "upload".
  const hasExplicitStageIndex = Number.isFinite(stageIndex)
    && (failedStageIndexToUiSegment(stageIndex) >= 0 || (stageIndex >= 0 && stageIndex <= 3));
  const hasExplicitStageLabel = isCanonicalFailedAtLabel(failedAtRaw)
    || failedStageIndexFromLabel(failedAtRaw) >= 0;

  const verboseDetail = failureDetail && isVerboseFailureText(failureDetail) ? failureDetail : null;
  const shouldUseProcessingContext =
    isWatchdogGenericFailureDetail(polledFailedAt)
    || isWatchdogGenericFailureDetail(failureDetail)
    || (
      extracted != null
      && extracted > 0
      && !isSpecificUserFacingFailureDetail(failureDetail, failedAt)
    );
  if (shouldUseProcessingContext) {
    const ctx = resolveFailureStageFromProcessingContext({
      proc: {
        stage: pipelineStage,
        framesExtracted: extracted,
        framesRegistered: registered,
      },
      progressPercent: pct,
      progressMessage,
    });
    failedAtRaw = ctx.failedAtRaw;
    stageIndex = ctx.failedStageIndex;
  } else if (sparseFromText) {
    failedAtRaw = CANONICAL_FAILED_AT.SFM;
    stageIndex = 3;
    pct = SFM_PERCENT_END;
  } else if (hasExplicitStageIndex) {
    failedAtRaw = canonicalLabelFromStageIndex(normalizeFailedStageIndexToUiSegment(stageIndex, failedAtRaw));
    stageIndex = normalizeFailedStageIndexToUiSegment(stageIndex, failedAtRaw);
  } else if (hasExplicitStageLabel) {
    const canonical = canonicalFailedAtFromRaw(failedAtRaw, canonicalStageOpts);
    failedAtRaw = canonical;
    stageIndex = normalizeFailedStageIndexToUiSegment(
      coerceFailedStageIndex(stageIndex, canonical, pct),
      canonical,
    );
  } else if (verboseDetail && isSpecificUserFacingFailureDetail(verboseDetail, failedAtRaw)) {
    failedAtRaw = canonicalFailedAtFromRaw(verboseDetail, canonicalStageOpts);
    stageIndex = normalizeFailedStageIndexToUiSegment(
      coerceFailedStageIndex(stageIndex, failedAtRaw, pct),
      failedAtRaw,
    );
  } else {
    const canonical = canonicalFailedAtFromRaw(failedAtRaw, canonicalStageOpts);
    failedAtRaw = canonical;
    stageIndex = normalizeFailedStageIndexToUiSegment(
      coerceFailedStageIndex(stageIndex, canonical, pct),
      canonical,
    );
  }

  const uiStageIndex = normalizeFailedStageIndexToUiSegment(stageIndex, failedAtRaw);
  const clampedPct = clampFailedPercentForStageIndex(uiStageIndex, pct, failedAtRaw);
  const sparseCounts = resolveSfmTooSparseFrameCounts({
    framesRegistered: registered,
    framesExtracted: extracted,
    failureDetail,
    progressMessage,
    polledFailedAt,
  });
  const internalCode = String(failureCode || '').trim().toUpperCase();
  const showSfmFailureHint = (
    sparseFromText
    || isSfmTooSparseFromFrameCounts(extracted, registered)
    || (uiStageIndex === 2 && sparseCounts != null)
  ) && !SFM_INFRA_SPARSE_HINT_SUPPRESS_CODES.has(internalCode);

  const hasFailureEvidence = isTerminalStatus
    || hasUploadErr
    || sparseFromText
    || isSpecificUserFacingFailureDetail(failureDetail, failedAt)
    || isSfmTooSparseFromFrameCounts(extracted, registered);
  const internalWhileRunning = INTERNAL_FAILURE_CODES.has(internalCode)
    && isActivePipelineRunStatus(status);
  const polledFailureMsg = polledFailedAt != null && String(polledFailedAt).trim()
    ? String(polledFailedAt).trim()
    : null;

  return {
    isFailed: isTerminalStatus
      || hasUploadErr
      || terminalSparseFrames
      || internalWhileRunning
      || !!polledFailureMsg
      || (hasFailureEvidence && !isActivePipelineRunStatus(status)),
    hasFailureEvidence,
    failedStageIndex: uiStageIndex >= 0 ? uiStageIndex : -1,
    failedStageLabel: uiStageIndex >= 0 ? stageIndexToLabel(uiStageIndex) : 'UNKNOWN',
    failedAt: failedAtRaw,
    failedPercent: clampedPct,
    isSfmTooSparse: showSfmFailureHint,
    sparseCounts,
    failureDetailText: failureDetail,
    showSfmFailureHint,
  };
}

/** Parse matched/total frame counts from SfM-too-sparse failure text. */
export function parseSfmTooSparseFrameCounts(failureDetail) {
  if (!failureDetail || typeof failureDetail !== 'string') return null;
  const registered = failureDetail.match(/\((\d+)\/(\d+)\s+images\s+registered\)/i);
  if (registered) {
    return { matched: Number(registered[1]), total: Number(registered[2]) };
  }
  const matchedFrames = failureDetail.match(/matched\s+(\d+)\s*\/\s*(\d+)\s+frames?/i);
  if (matchedFrames) {
    return { matched: Number(matchedFrames[1]), total: Number(matchedFrames[2]) };
  }
  const matchedImages = failureDetail.match(/matched\s+(\d+)\s*\/\s*(\d+)\s+images?/i);
  if (matchedImages) {
    return { matched: Number(matchedImages[1]), total: Number(matchedImages[2]) };
  }
  const imagesMatched = failureDetail.match(/(\d+)\s*\/\s*(\d+)\s+images?\s+matched/i);
  if (imagesMatched) {
    return { matched: Number(imagesMatched[1]), total: Number(imagesMatched[2]) };
  }
  const slashPair = failureDetail.match(/too sparse[^0-9]*(\d+)\s*\/\s*(\d+)/i);
  if (slashPair) {
    return { matched: Number(slashPair[1]), total: Number(slashPair[2]) };
  }
  return null;
}

/** Collect matched/total counts from structured fields or failure-related strings. */
export function resolveSfmTooSparseFrameCounts({
  framesRegistered = null,
  framesExtracted = null,
  failureDetail = null,
  progressMessage = null,
  polledFailedAt = null,
  failureHoverExplanation = null,
} = {}) {
  const registered = coerceFrameInt(framesRegistered);
  const extracted = coerceFrameInt(framesExtracted);
  if (registered != null && extracted != null) {
    return { matched: registered, total: extracted };
  }
  const sources = [failureDetail, progressMessage, polledFailedAt, failureHoverExplanation];
  for (const raw of sources) {
    const counts = parseSfmTooSparseFrameCounts(raw);
    if (counts) return counts;
  }
  return null;
}

/** Whether LoadingUI should show the SfM-too-sparse warning callout (not the generic orbit/support hints). */
export function shouldShowSfmTooSparseHint({
  failureDetail = null,
  failureHoverExplanation = null,
  failedAt = null,
  progressMessage = null,
  polledFailedAt = null,
  failedStageIndex = -1,
  framesRegistered = null,
  framesExtracted = null,
  proc = {},
  failureCode = null,
} = {}) {
  if (isGpuQueueTimeoutFailure({
    failedAt,
    failureDetail,
    failedStageIndex,
    proc,
    failureCode,
  })) {
    return false;
  }
  if (isSfmTooSparseFromFrameCounts(framesExtracted, framesRegistered)) {
    return true;
  }
  if (isSfmTooSparseFailure({
    failureDetail,
    failureHoverExplanation,
    failedAt,
    progressMessage,
    polledFailedAt,
  })) {
    return true;
  }
  const parsedCounts = resolveSfmTooSparseFrameCounts({
    framesRegistered,
    framesExtracted,
    failureDetail,
    progressMessage,
    polledFailedAt,
    failureHoverExplanation,
  });
  if (
    parsedCounts
    && Number.isFinite(parsedCounts.matched)
    && parsedCounts.matched < MIN_REGISTERED_CAMERAS
  ) {
    return true;
  }
  if (failedStageIndexToUiSegment(failedStageIndex) !== 2) return false;
  if (parsedCounts) {
    return true;
  }
  const short = getFailureShortReason(failureDetail, failedStageIndex);
  return typeof short === 'string' && short.toLowerCase().includes('sparse');
}

export function isDispatchFailure({
  failedAt = null,
  failureDetail = null,
  failedStageIndex = -1,
  proc = {},
} = {}) {
  const at = String(failedAt || '').trim().toLowerCase();
  if (at === 'dispatch') return true;
  if (failedStageIndex === -1 && at === 'dispatch') return true;
  const detail = String(failureDetail || '').trim().toLowerCase();
  if (detail.includes('dispatch failed') || detail.includes('job submission failed')) return true;
  if (detail.includes('dispatch was lost')) return true;
  if (detail.includes('never started on a gpu worker')) return true;
  const diagnostics = proc.diagnostics && typeof proc.diagnostics === 'object'
    ? proc.diagnostics
    : {};
  if (String(diagnostics.watchdogKind || '').trim() === 'orphan_no_job') return true;
  return false;
}

export function isGpuWorkerUnavailableFailure({
  failedAt = null,
  failureDetail = null,
} = {}) {
  const detail = String(failureDetail || '').trim();
  const n = detail.toLowerCase();
  const at = String(failedAt || '').trim();
  return (
    detail === GPU_WORKER_UNAVAILABLE_FAILED_AT
    || at === GPU_WORKER_UNAVAILABLE_FAILED_AT
    || n.includes('no healthy gpu worker')
    || n.includes('gpu worker unavailable')
    || n.includes('no active worker')
    || n.includes('no workers available')
    || n.includes('workers are busy')
    || (at === 'Dispatch' && (n.includes('worker') || n.includes('queue')))
  );
}

/**
 * Strict dispatch / queue / worker-timeout classifier — not sparse SfM or generic internal errors.
 */
export function isGpuQueueTimeoutFailure({
  failedAt = null,
  failureDetail = null,
  failedStageIndex = -1,
  proc = {},
  failureCode = null,
} = {}) {
  const code = String(failureCode ?? proc.failureCode ?? '').trim().toUpperCase();
  if (code === SPARSE_SFM_FAILURE_CODE) return false;

  if (isDispatchFailure({ failedAt, failureDetail, failedStageIndex, proc })) {
    return true;
  }
  if (isGpuWorkerUnavailableFailure({ failedAt, failureDetail })) {
    return true;
  }

  const detail = String(failureDetail ?? proc.failureDetail ?? '').trim().toLowerCase();
  if (!detail) return false;

  const at = String(failedAt ?? proc.failedAt ?? '').trim().toLowerCase();
  const dispatchPhase = at === 'dispatch' || at === '' || failedStageIndex <= 0;
  if (
    !dispatchPhase
    && !detail.includes('queue')
    && !detail.includes('worker')
    && !detail.includes('gpu')
  ) {
    return false;
  }

  return GPU_QUEUE_TIMEOUT_DETAIL_PATTERNS.some((pattern) => detail.includes(pattern));
}

/**
 * Watchdog / queue infra failures that self-heal when the worker resumes or dispatch retries.
 */
export function isRecoverableInfraFailure({
  failedAt = null,
  failureDetail = null,
  proc = {},
} = {}) {
  const diagnostics = proc.diagnostics && typeof proc.diagnostics === 'object'
    ? proc.diagnostics
    : {};
  const watchdogKind = String(diagnostics.watchdogKind || '').trim();
  if (watchdogKind === 'orphan_no_job') return true;
  if (watchdogKind === 'no_worker_heartbeat') return true;
  if (watchdogKind === 'wall_clock_cap' || watchdogKind === 'completed_without_callback') {
    return false;
  }
  if (isGpuWorkerUnavailableFailure({ failedAt, failureDetail })) return true;

  const detail = String(failureDetail ?? proc.failureDetail ?? '').trim().toLowerCase();
  if (detail.includes('maximum processing time')) return false;
  if (detail.includes('without delivering results')) return false;
  if (detail.includes('dispatch was lost')) return true;
  if (
    detail.includes('no healthy gpu worker')
    || detail.includes('gpu worker unavailable')
    || detail.includes('no active worker')
    || detail.includes('no workers available')
    || detail.includes('workers are busy')
  ) return true;

  const at = String(failedAt ?? proc.failedAt ?? '').trim().toLowerCase();
  return at === 'dispatch';
}

function timestampToMs(value) {
  if (value == null) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value instanceof Date) return value.getTime();
  return null;
}

/** True when worker liveness is recent enough to override a stale infra failure flag. */
export function hasRecentWorkerLiveness(progress, maxAgeMs = 90_000) {
  if (!progress || typeof progress !== 'object') return false;
  const now = Date.now();
  for (const key of ['workerHeartbeatAt', 'lastAdvanceAt', 'updatedAt']) {
    const ms = timestampToMs(progress[key]);
    if (ms != null && now - ms <= maxAgeMs) return true;
  }
  return false;
}

/**
 * Suppress failed UI when Firestore still says failed but the worker clearly continued
 * after a recoverable orphan/dispatch/GPU-queue failure.
 */
export function shouldSuppressStaleInfraFailureUi({
  processing = {},
  progress = {},
} = {}) {
  const status = String(processing.status || '').toLowerCase();
  if (status !== 'failed') return false;
  if (!isRecoverableInfraFailure({
    failedAt: processing.failedAt,
    failureDetail: processing.failureDetail,
    proc: processing,
  })) {
    return false;
  }
  const { framesExtracted } = readCaptureFrameFields(processing);
  if (framesExtracted != null && framesExtracted > 0) return true;
  if (String(progress.runpodJobId || '').trim()) return true;
  if (hasRecentWorkerLiveness(progress)) return true;
  return false;
}

export function isWatchdogGenericFailure({ failureDetail = null } = {}) {
  const detail = String(failureDetail || '').trim().toLowerCase();
  if (!detail) return false;
  return (
    detail.includes('maximum processing time')
    || detail.includes('dispatch was lost')
    || detail.includes('without delivering results')
    || detail.includes('worker finished without')
    || detail.includes('pipeline progress stalled')
    || detail.includes('finalization did not reach viewer-ready')
  );
}

export function isUploadNetworkFailure({
  failureDetail = null,
  uploadError = null,
  failedStageIndex = -1,
} = {}) {
  if (failedStageIndex === 0) return true;
  const candidates = [failureDetail, uploadError];
  for (const raw of candidates) {
    if (raw == null || typeof raw !== 'string') continue;
    const n = raw.toLowerCase();
    if (
      n.includes('upload interrupted')
      || n.includes('upload lost connection')
      || n.includes('lost connection before it finished')
      || (n.includes('upload') && (n.includes('network') || n.includes('offline') || n.includes('connection')))
    ) {
      return true;
    }
  }
  return false;
}

export function isStorageCapFailure({ failureDetail = null, uploadError = null } = {}) {
  const candidates = [failureDetail, uploadError];
  for (const raw of candidates) {
    if (raw == null || typeof raw !== 'string') continue;
    const n = raw.toLowerCase();
    if (
      n.includes('storage is full')
      || n.includes('storage cap')
      || n.includes('capture storage')
      || (n.includes('storage') && n.includes('quota'))
    ) {
      return true;
    }
  }
  return false;
}

export function isWorkerStoppedFailure({ failureDetail = null, polledFailedAt = null } = {}) {
  const candidates = [failureDetail, polledFailedAt];
  for (const raw of candidates) {
    if (raw == null || typeof raw !== 'string') continue;
    const n = raw.toLowerCase();
    if (
      n.includes('gpu worker timed out')
      || n.includes('execution time limit')
      || n.includes('worker stopped unexpectedly')
      || n.includes('runpod status: failed')
      || n.includes('runpod status: error')
      || n.includes('runpod status: timed_out')
      || n.includes('process killed')
      || n.includes('gpu memory full')
      || n.includes('out of memory')
    ) {
      return true;
    }
  }
  return false;
}

/** Sparse SfM retry UI variant from processing metadata. */
export function resolveSparseRetryVariant({ processing = {}, showSfmFailureHint = false } = {}) {
  if (!showSfmFailureHint) return null;
  const method = String(processing.sfmMatchingMethod || processing.sfm_matching_method || '')
    .trim()
    .toLowerCase();
  if (method === 'exhaustive') return 'precisionFailed';
  if (method === 'vocab_tree' || method === 'vocabtree' || method === 'sequential') return 'initial';
  // Matcher field can briefly disappear on tab refocus — do not assume vocab_tree.
  return null;
}

/** HUD / overlay title for a failed capture stage. */
export function resolveCaptureFailureTitle({
  sparseRetryVariant = null,
  failedStageIndex = -1,
  dispatchFailure = false,
  gpuQueueTimeoutFailure = false,
  fallbackTitle = 'FAILED',
} = {}) {
  if (gpuQueueTimeoutFailure) return GPU_QUEUE_TIMEOUT_FAILURE_TITLE;
  if (sparseRetryVariant === 'precisionFailed') {
    return 'FULL ANALYSIS FAILED';
  }
  if (dispatchFailure) return 'PROCESSING NOT STARTED';
  if (failedStageIndex >= 0) {
    return `FAILED AT ${stageIndexToLabel(failedStageIndex)}`;
  }
  return fallbackTitle;
}

/**
 * Ops/diagnostic text shapes that must never reach the customer-facing hint box:
 * Python tracebacks, exit codes, RunPod job chatter, container paths, CUDA errors.
 */
const OPS_ONLY_FAILURE_TEXT = [
  /exited with code/i,
  /\btraceback\b/i,
  /\b\w*(?:Error|Exception)\b\s*:/,
  /\brunpod\b/i,
  /\bsubprocess\b/i,
  /\bcuda\b/i,
  /\bsegmentation fault\b/i,
  /\bexit(?:ed|_)?\s*status\b/i,
  /(?:^|\s)(?:\/workspace|\/usr\/|\/opt\/|[A-Za-z]:\\)/,
  /\.py[:"\s]/i,
];

function looksLikeOpsOnlyFailureText(text) {
  return OPS_ONLY_FAILURE_TEXT.some((re) => re.test(text));
}

function resolveInsvFailureCallout(failureDetail) {
  if (failureDetail == null || typeof failureDetail !== 'string') return null;
  const n = stripErrorPrefix(failureDetail).toLowerCase();
  if (n.includes('insv ingest is not enabled')) return FAILURE_CALLOUT_MESSAGES.INSV_INGEST_DISABLED;
  if (n.includes('insv capture is missing raw file metadata') || n.includes('insvrawpaths')) {
    return FAILURE_CALLOUT_MESSAGES.INSV_METADATA_MISSING;
  }
  if (n.includes('raw insv not found in storage')) return FAILURE_CALLOUT_MESSAGES.INSV_RAW_MISSING;
  if (
    n.includes('insv stitch failed')
    || n.includes('ffmpeg insv stitch failed')
    || n.includes('mediasdk stitch failed')
    || n.includes('stitched output failed equirect validation')
    || n.includes('concatenated stitched output failed equirect validation')
    || n.includes('insv segments do not align')
    || n.includes('dual-track clip detected')
  ) {
    return FAILURE_CALLOUT_MESSAGES.INSV_STITCH_FAILED;
  }
  return null;
}

/**
 * Full user-facing failure copy for LoadingUI info callout (below stage indicators).
 * Returns layman Message Matrix copy in the grey hint box — never short pill labels.
 */
export function resolveFailureDetailCalloutText({
  failureDetail = null,
  failureCode = null,
  failedAt = null,
  uploadError = null,
  polledFailedAt = null,
  progressMessage = null,
  failedStageIndex = -1,
  captureProfile = null,
  sfmBackend = null,
} = {}) {
  const insvCallout = resolveInsvFailureCallout(failureDetail);
  if (insvCallout) return insvCallout;

  if (isStorageCapFailure({ failureDetail, uploadError })) {
    return FAILURE_CALLOUT_MESSAGES.STORAGE_CAP;
  }
  if (isUploadNetworkFailure({ failureDetail, uploadError, failedStageIndex })) {
    return FAILURE_CALLOUT_MESSAGES.UPLOAD_NETWORK;
  }
  if (isGpuQueueTimeoutFailure({
    failedAt,
    failureDetail,
    failedStageIndex,
    failureCode,
  })) {
    return FAILURE_CALLOUT_MESSAGES.GPU_UNAVAILABLE;
  }
  if (isGpuWorkerUnavailableFailure({ failedAt, failureDetail })) {
    return FAILURE_CALLOUT_MESSAGES.GPU_UNAVAILABLE;
  }
  if (isTransientFailure({ failureCode, failureDetail })) {
    return TRANSIENT_FAILURE_MESSAGE;
  }
  if (isDispatchFailure({ failedAt, failureDetail, failedStageIndex })) {
    return FAILURE_CALLOUT_MESSAGES.DISPATCH;
  }
  if (
    isWorkerStoppedFailure({ failureDetail, polledFailedAt })
    || isWatchdogGenericFailure({ failureDetail })
  ) {
    return FAILURE_CALLOUT_MESSAGES.WORKER_STOPPED;
  }
  if (shouldShowSfmTooSparseHint({
    failureDetail,
    failedAt,
    progressMessage,
    polledFailedAt,
    failedStageIndex,
  })) {
    return null;
  }
  if (isUnexpectedInternalFailure({ failureCode, failureDetail, failedStageIndex })) {
    return resolveUnexpectedInternalFailureMessage({ failureCode, failureDetail });
  }
  // Checked before the stage-label/candidate paths: text like "GaussianTraining exited
  // with code 1" otherwise reads as a stage label and earns unrelated recapture advice.
  if (failureDetail && looksLikeOpsOnlyFailureText(stripErrorPrefix(failureDetail))) {
    return UNEXPECTED_INTERNAL_FAILURE_MESSAGE;
  }

  const candidates = [
    failureDetail,
    polledFailedAt,
    uploadError,
    progressMessage,
    failedAt,
  ];
  for (const raw of candidates) {
    if (raw == null || typeof raw !== 'string') continue;
    const text = stripErrorPrefix(raw);
    if (!text || /^internal$/i.test(text) || !isVerboseFailureText(text)) continue;
    if (/^unknown:\s/i.test(text)) continue;
    if (/worker crashed:/i.test(text)) return FAILURE_CALLOUT_MESSAGES.WORKER_STOPPED;
    if (/processing stalled with no output/i.test(text)) return TRANSIENT_FAILURE_MESSAGE;
    if (looksLikeOpsOnlyFailureText(text)) return UNEXPECTED_INTERNAL_FAILURE_MESSAGE;
    return text;
  }
  if (failedStageIndexToUiSegment(failedStageIndex) === 2) {
    return sfmRecaptureHintForProfile(captureProfile, { sfmBackend });
  }
  if (failedStageIndexToUiSegment(failedStageIndex) === 1) {
    const profile = resolveCaptureProfileForFailureHint({ captureProfile, sfmBackend });
    if (profile === 'equirect_360') {
      return sfmRecaptureHintForProfile(captureProfile, { sfmBackend });
    }
  }
  return null;
}

/**
 * Resolve canonical failure snapshot from poll data first, then Firestore metadata.
 * Returns { failedStageIndex, failedPercent, failedAt, isSfmTooSparse, sparseCounts } or null when not failed.
 */
export function resolveFailedSnapshot(polledData, captureMetadata) {
  const polledStatus = polledData?.status;
  const firestoreStatus = captureMetadata?.processing?.status;
  const progressMessage = captureMetadata?.progress?.message || polledData?.message || '';
  const progressPercent = captureMetadata?.progress?.percent ?? polledData?.percent ?? 0;
  const polledFailMsg = polledData?.failedAt != null && String(polledData.failedAt).trim()
    ? String(polledData.failedAt).trim()
    : null;
  const pollIndicatesFailure = polledStatus === 'failed' || polledStatus === 'cancelled';
  const frameFields = readCaptureFrameFields(captureMetadata?.processing || {});
  const terminalEvidence = hasCaptureTerminalFailureEvidence({
    processingStatus: firestoreStatus,
    failedAt: captureMetadata?.processing?.failedAt,
    failureDetail: captureMetadata?.processing?.failureDetail,
    progressMessage,
    progress: captureMetadata?.progress,
    proc: captureMetadata?.processing,
  });

  if (
    isActivePipelineRunStatus(firestoreStatus)
    && !pollIndicatesFailure
    && !polledFailMsg
    && !terminalEvidence
  ) {
    return null;
  }

  const isFailed = pollIndicatesFailure
    || firestoreStatus === 'failed'
    || firestoreStatus === 'cancelled'
    || !!(captureMetadata?.processing?.failedAt)
    || !!polledFailMsg
    || terminalEvidence;

  if (!isFailed) return null;

  const canonical = resolveCanonicalFailure({
    processingStatus: pollIndicatesFailure ? polledStatus : firestoreStatus,
    failedAt: captureMetadata?.processing?.failedAt ?? polledFailMsg,
    failureDetail: captureMetadata?.processing?.failureDetail,
    failureCode: captureMetadata?.processing?.failureCode,
    sfmMatchingMethod: captureMetadata?.processing?.sfmMatchingMethod
      ?? captureMetadata?.processing?.sfm_matching_method,
    failedStageIndex: captureMetadata?.processing?.failedStageIndex ?? polledData?.failedStageIndex,
    failedPercent: captureMetadata?.processing?.failedPercent ?? polledData?.failedPercent,
    progressMessage,
    progressPercent,
    polledFailedAt: polledData?.failedAt ?? polledFailMsg,
    polledFailedStageIndex: polledData?.failedStageIndex,
    polledFailedPercent: polledData?.failedPercent,
    framesRegistered: frameFields.framesRegistered,
    framesExtracted: frameFields.framesExtracted,
  });

  const processing = captureMetadata?.processing || {};
  const gpuQueueTimeoutFailure = isGpuQueueTimeoutFailure({
    failedAt: processing.failedAt ?? polledFailMsg,
    failureDetail: processing.failureDetail,
    failedStageIndex: canonical.failedStageIndex,
    proc: processing,
    failureCode: processing.failureCode,
  });

  return {
    failedAt: canonical.failedAt,
    failedStageIndex: gpuQueueTimeoutFailure ? -1 : canonical.failedStageIndex,
    failedPercent: gpuQueueTimeoutFailure ? 0 : canonical.failedPercent,
    isSfmTooSparse: gpuQueueTimeoutFailure ? false : canonical.isSfmTooSparse,
    sparseCounts: gpuQueueTimeoutFailure ? null : canonical.sparseCounts,
    showSfmFailureHint: gpuQueueTimeoutFailure ? false : canonical.showSfmFailureHint,
    gpuQueueTimeoutFailure,
  };
}

export function getErrorMessage(failedAt, uploadError, backendOffline, failedStageIndex = -1) {
  if (backendOffline) return formatFailurePillLabel('Servers offline');
  return resolveFailureDisplayMessage({
    failureDetail: uploadError || null,
    failedAt,
    failedStageIndex,
  });
}